import { joinNavItemGroups } from '@billy/design-system'
import { TFunction } from 'i18next'

import { convertibleQuoteStatuses } from '../../../constants/convertibleQuoteStatuses'
import { nonEditableQuoteStatuses } from '../../../constants/nonEditableQuoteStatuses'
import { quoteSentStatusEvents } from '../../../constants/quoteSentStatusEvents'
import { quoteStatusToAllowedTransitions } from '../../../constants/quoteStatusToAllowedTransitions'
import { QuoteStatus } from '../../../enums/quoteStatus'
import { Quote } from '../../../types/quotes/quote'
import { QuoteEvent } from '../../../types/quotes/quoteEvent'
import { getQuoteConvertEligibility } from '../../../utils/depositInvoices/getQuoteConvertEligibility'
import { getQuoteMarkabilityStatus } from '../../../utils/getQuoteMarkabilityStatus'
import { QuoteStatusOption } from '../elements/QuoteStatusOption'
import { QuotesTableStaticActionType } from '../enums/quotesTableStaticActionType'
import { QuotesTableStaticActionItem } from '../types/quotesTableStaticActionItem'
import { getApprovedInvoiceId } from './getApprovedInvoiceId'
import { getEffectiveQuotesTableItems } from './getEffectiveQuotesTableItems'
import { getViewLinkedInvoiceUrl } from './getViewLinkedInvoiceUrl'

interface GetBaseQuotesTableStaticActionsParams {
  canCreateDepositInvoice: boolean
  duplicateUrl: string
  editUrl: string
  isDraft: boolean
  isMarkableAsSent: boolean
  isMarkableAsUnsent: boolean
  isQuotePartiallyInvoiced: boolean
  quoteStatus: QuoteStatus | undefined
  t: TFunction
  viewLinkedInvoiceUrl?: string
}

export interface GetQuotesTableStaticActionsParams
  extends Omit<
    GetBaseQuotesTableStaticActionsParams,
    'isMarkableAsSent' | 'isMarkableAsUnsent' | 'canCreateDepositInvoice' | 'isQuotePartiallyInvoiced'
  > {
  fetchedQuoteEvents: QuoteEvent[]
  fetchedQuoteInvoiceState?: Quote['invoiceState']
  fetchedQuoteStatus: QuoteStatus | undefined
  organizationUrl: string | undefined
  isDownloading: boolean
  isProcessing: boolean
  isProcessingSendAsEmail: boolean
}

const getBaseQuotesTableStaticActions = ({
  canCreateDepositInvoice,
  duplicateUrl,
  editUrl,
  isDraft,
  isMarkableAsSent,
  isMarkableAsUnsent,
  isQuotePartiallyInvoiced,
  quoteStatus,
  t,
  viewLinkedInvoiceUrl,
}: GetBaseQuotesTableStaticActionsParams): QuotesTableStaticActionItem[] => {
  const editActionItem: QuotesTableStaticActionItem = {
    children: t('quotes.table.static_actions.edit'),
    iconLeft: 'squarePen',
    id: QuotesTableStaticActionType.Edit,
    to: editUrl,
    value: QuotesTableStaticActionType.Edit,
  }
  const duplicateActionItem: QuotesTableStaticActionItem = {
    children: t('quotes.table.static_actions.duplicate'),
    iconLeft: 'copy',
    id: QuotesTableStaticActionType.Duplicate,
    to: duplicateUrl,
    value: QuotesTableStaticActionType.Duplicate,
  }
  const deleteActionItem: QuotesTableStaticActionItem = {
    children: t('quotes.table.static_actions.delete'),
    iconLeft: 'trash',
    id: QuotesTableStaticActionType.Delete,
    value: QuotesTableStaticActionType.Delete,
    variant: 'danger',
  }

  const isInvoiced = quoteStatus === QuoteStatus.Invoiced
  const canDelete = quoteStatus !== QuoteStatus.Deleted && !isInvoiced

  if (isDraft) {
    return joinNavItemGroups([[editActionItem, duplicateActionItem], [deleteActionItem]])
  }

  const canEdit = quoteStatus !== undefined && !nonEditableQuoteStatuses.includes(quoteStatus)
  const canConvertToInvoice =
    quoteStatus !== undefined && convertibleQuoteStatuses.includes(quoteStatus) && !isQuotePartiallyInvoiced

  const convertToDepositInvoiceActionItem: QuotesTableStaticActionItem = {
    children: t('external_invoices.deposit_invoice.create_deposit_invoice'),
    iconLeft: 'files',
    id: QuotesTableStaticActionType.ConvertToDepositInvoice,
    value: QuotesTableStaticActionType.ConvertToDepositInvoice,
  }
  const convertToFinalInvoiceActionItem: QuotesTableStaticActionItem = {
    children: t('external_invoices.deposit_invoice.create_final_invoice'),
    iconLeft: 'fileText',
    id: QuotesTableStaticActionType.ConvertToFinalInvoice,
    value: QuotesTableStaticActionType.ConvertToFinalInvoice,
  }
  const convertToInvoiceActionItem: QuotesTableStaticActionItem = {
    children: t('quotes.table.static_actions.create_invoice'),
    iconLeft: 'fileText',
    id: QuotesTableStaticActionType.ConvertToInvoice,
    value: QuotesTableStaticActionType.ConvertToInvoice,
  }
  const viewLinkedInvoiceActionItem: QuotesTableStaticActionItem = {
    children: t('quotes.table.static_actions.view_linked_invoice'),
    iconLeft: 'eye',
    id: QuotesTableStaticActionType.ViewLinkedInvoice,
    to: viewLinkedInvoiceUrl,
    value: QuotesTableStaticActionType.ViewLinkedInvoice,
  }
  const downloadActionItem: QuotesTableStaticActionItem = {
    children: t('quotes.table.static_actions.download'),
    iconLeft: 'download',
    id: QuotesTableStaticActionType.Download,
    value: QuotesTableStaticActionType.Download,
  }

  const sendByEmailItem: QuotesTableStaticActionItem = {
    children: t('quotes.table.static_actions.send_by_email'),
    iconLeft: 'mail',
    id: QuotesTableStaticActionType.SendByEmail,
    value: QuotesTableStaticActionType.SendByEmail,
  }

  const canSendByEmail = quoteStatus !== undefined && quoteStatus !== QuoteStatus.Deleted

  const markAsSentActionItem: QuotesTableStaticActionItem = {
    children: t('quotes.view.mark_as_sent'),
    iconLeft: 'paperPlane',
    id: QuotesTableStaticActionType.MarkAsSent,
    value: QuotesTableStaticActionType.MarkAsSent,
  }

  const markAsUnsentActionItem: QuotesTableStaticActionItem = {
    children: t('quotes.view.mark_as_unsent'),
    iconLeft: 'paperPlane',
    id: QuotesTableStaticActionType.MarkAsUnsent,
    value: QuotesTableStaticActionType.MarkAsUnsent,
  }

  const allowedTransitions = quoteStatus !== undefined ? quoteStatusToAllowedTransitions[quoteStatus] : []
  const canChangeStatus = allowedTransitions.length > 0
  const changeStatusActionItem: QuotesTableStaticActionItem = {
    children: t('quotes.table.static_actions.change_status'),
    id: QuotesTableStaticActionType.ChangeStatus,
    value: QuotesTableStaticActionType.ChangeStatus,
    subItems: allowedTransitions.map((nextStatus) => ({
      children: <QuoteStatusOption status={nextStatus} />,
      id: nextStatus,
      value: nextStatus,
    })),
  }

  return joinNavItemGroups([
    [
      ...(canCreateDepositInvoice ? [convertToDepositInvoiceActionItem] : []),
      ...(isQuotePartiallyInvoiced ? [convertToFinalInvoiceActionItem] : []),
      ...(canConvertToInvoice ? [convertToInvoiceActionItem] : []),
      ...(viewLinkedInvoiceUrl ? [viewLinkedInvoiceActionItem] : []),
    ],
    [...(canEdit ? [editActionItem] : []), ...(canChangeStatus ? [changeStatusActionItem] : [])],
    [...(canSendByEmail ? [sendByEmailItem] : []), downloadActionItem],
    [
      ...(isMarkableAsSent ? [markAsSentActionItem] : []),
      ...(isMarkableAsUnsent ? [markAsUnsentActionItem] : []),
      duplicateActionItem,
    ],
    canDelete ? [deleteActionItem] : [],
  ])
}

export const getQuotesTableStaticActions = ({
  duplicateUrl,
  editUrl,
  isDraft,
  quoteStatus,
  t,
  fetchedQuoteEvents,
  fetchedQuoteInvoiceState,
  fetchedQuoteStatus,
  organizationUrl,
  isDownloading,
  isProcessing,
  isProcessingSendAsEmail,
}: GetQuotesTableStaticActionsParams): QuotesTableStaticActionItem[] => {
  // The list endpoint is a slim projection that omits status.events and the linked-invoice
  // id, so the fetched* inputs only carry data once the selected quote has been fetched on
  // menu open. Callers must gate invocation behind their own loading state: undefined
  // post-fetch inputs are treated as "not applicable", not "still loading".

  // Prefer the freshly fetched status, falling back to the stale list status only
  // pre-fetch / on fetch error.
  const effectiveQuoteStatus = fetchedQuoteStatus ?? quoteStatus
  const viewLinkedInvoiceUrl = getViewLinkedInvoiceUrl(getApprovedInvoiceId(fetchedQuoteEvents), organizationUrl)

  const hasSentEvents = fetchedQuoteEvents.some((event) => quoteSentStatusEvents.includes(event.eventType))
  const { isMarkableAsSent, isMarkableAsUnsent } = getQuoteMarkabilityStatus(fetchedQuoteStatus, hasSentEvents)
  const convertEligibilityData = { invoiceState: fetchedQuoteInvoiceState, quoteStatus: effectiveQuoteStatus }
  const { canCreateDepositInvoice, isQuotePartiallyInvoiced } = getQuoteConvertEligibility(convertEligibilityData)

  const baseActionItems = getBaseQuotesTableStaticActions({
    canCreateDepositInvoice,
    duplicateUrl,
    editUrl,
    isDraft,
    isMarkableAsSent,
    isMarkableAsUnsent,
    isQuotePartiallyInvoiced,
    quoteStatus: effectiveQuoteStatus,
    t,
    viewLinkedInvoiceUrl,
  })

  return getEffectiveQuotesTableItems(baseActionItems, { isDownloading, isProcessing, isProcessingSendAsEmail })
}
